import { ref } from 'vue'
import * as Sentry from '@sentry/vue'
let firebaseInit
async function ensureFirebase() {
    if (!firebaseInit) {
        firebaseInit = await import('../firebase/init')
    }
    return firebaseInit
}
import notificationService from '../services/api/notification.service'

let cachedUserService = null
const getUserService = async () => {
    if (cachedUserService) return cachedUserService
    const { default: UserService } = await import('@/services/api/user.service')
    cachedUserService = UserService
    return cachedUserService
}

const reportNotificationError = (error, context, extra = {}) => {
    try {
        const err =
            error instanceof Error
                ? error
                : new Error(
                      typeof error === 'string' ? error : JSON.stringify(error)
                  )
        Sentry?.captureException(err, {
            tags: {
                area: 'notifications',
                context,
            },
            extra,
        })
    } catch (_) {}
}

// State that persists between component instances
const notificationsEnabled = ref(false)
const currentFcmToken = ref(null)
const isTokenRegisteredWithBackend = ref(false)
const registeredUserId = ref(null)
const disableForegroundUI = ref(true)
const notificationCallbacks = []
const isBrowserSupported = ref(true)

// Handle foreground message (when app is open)
const handleForegroundMessage = async (payload) => {
    try {
        const data = payload?.data || {}

        // For compatibility, still derive basic notification data when present
        if (payload.notification && !disableForegroundUI.value) {
            const notificationData = {
                title: payload.notification.title,
                body: payload.notification.body,
                actionUrl: data?.url || '/',
            }
            notificationCallbacks.forEach((callback) => {
                if (typeof callback === 'function') callback(notificationData)
            })
        }

        // If a studio percentage change request arrives, refresh activities so UI shows it immediately
        if (type === 'studio.percentage_change_request') {
            try {
                const { useActivityStore } = await import(
                    '@/stores/activity.store'
                )
                const activityStore = useActivityStore()
                await activityStore.fetchActivities({ force: true })
            } catch (e) {
                // non-fatal
            }
        }
    } catch (e) {
        // swallow errors
    }
}

// Set up message handler
let unsubscribeMessage = null
const markBrowserUnsupported = () => {
    notificationsEnabled.value = false
    currentFcmToken.value = null
    isTokenRegisteredWithBackend.value = false
    registeredUserId.value = null
    if (unsubscribeMessage) {
        try {
            unsubscribeMessage()
        } catch (_) {}
        unsubscribeMessage = null
    }
    isBrowserSupported.value = false
}

const setupMessageHandler = async () => {
    if (!isBrowserSupported.value) return
    const { onForegroundMessage } = await ensureFirebase()
    if (!unsubscribeMessage) {
        const unsubscribe = await onForegroundMessage(handleForegroundMessage)
        if (typeof unsubscribe === 'function') {
            unsubscribeMessage = unsubscribe
        }
    }
}

const ensureNotificationsSupported = async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        markBrowserUnsupported()
        return false
    }

    if (typeof Notification === 'undefined') {
        markBrowserUnsupported()
        return false
    }

    const ua = navigator.userAgent || navigator.vendor || ''
    const isIOSDevice =
        /iPad|iPhone|iPod/.test(ua) ||
        (ua.includes('Mac') &&
            typeof navigator.maxTouchPoints === 'number' &&
            navigator.maxTouchPoints > 1)

    const isStandalone = (() => {
        if (typeof window === 'undefined') return false
        try {
            return (
                window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true
            )
        } catch (_) {
            return false
        }
    })()

    if (isIOSDevice && !isStandalone) {
        markBrowserUnsupported()
        return false
    }

    try {
        const { isMessagingSupported } = await ensureFirebase()
        const supported = await isMessagingSupported()
        if (!supported) {
            markBrowserUnsupported()
            return false
        }
        isBrowserSupported.value = true
        return true
    } catch (error) {
        markBrowserUnsupported()
        reportNotificationError(error, 'ensure-notification-support')
        return false
    }
}

// Initialize by checking permission and token
const init = async () => {
    const supported = await ensureNotificationsSupported()
    if (!supported) {
        return false
    }

    await setupMessageHandler()

    // Check if Notification is defined (browser supports notifications)
    if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
    ) {
        // If permission is already granted, check if we have a token
        const { getFCMToken } = await ensureFirebase()
        const token = await getFCMToken()
        if (token) {
            currentFcmToken.value = token
            notificationsEnabled.value = true
            return true
        }
    }

    return notificationsEnabled.value
}

// Clean up
const cleanup = () => {
    if (unsubscribeMessage) {
        unsubscribeMessage()
        unsubscribeMessage = null
    }
}

// Register token with backend
const registerTokenWithBackend = async (token) => {
    try {
        console.log('Registering token with backend', token)
        await notificationService.registerToken(token)
        isTokenRegisteredWithBackend.value = true
        notificationsEnabled.value = true
        try {
            const { useAuthStore } = await import('@/stores/auth.store')
            const authStore = useAuthStore()
            registeredUserId.value = authStore.userId
        } catch (_) {
            registeredUserId.value = null
        }
        return true
    } catch (error) {
        console.error('Failed to register notification token:', error)
        reportNotificationError(error, 'register-token-with-backend')
        isTokenRegisteredWithBackend.value = false
        notificationsEnabled.value = false
        return false
    }
}

export function useNotifications() {
    // Request notification permission
    const requestPermission = async () => {
        try {
            const supported = await ensureNotificationsSupported()
            if (!supported) {
                return { success: false, reason: 'unsupported' }
            }
            const { requestNotificationPermission } = await ensureFirebase()
            const token = await requestNotificationPermission()

            if (!token) {
                notificationsEnabled.value = false
                return { success: false, reason: 'token-unavailable' }
            }

            // Store the token for future use regardless of registration success
            currentFcmToken.value = token

            // Register token with backend; treat failure as a hard failure so UI does not misreport state
            const registered = await registerTokenWithBackend(token)

            if (!registered) {
                try {
                    const UserService = await getUserService()
                    UserService.updateUser({
                        notificationsEnabled: false,
                    }).catch(() => {})
                } catch (error) {
                    reportNotificationError(
                        error,
                        'persist-disable-preference',
                        {
                            stage: 'enable-fallback',
                        }
                    )
                }

                return {
                    success: false,
                    token,
                    registeredWithBackend: false,
                }
            }

            // Persist preference on user document (best-effort)
            try {
                const UserService = await getUserService()
                // This mirrors UI intent so MyAccount stays in sync with DB
                UserService.updateUser({ notificationsEnabled: true }).catch(
                    () => {}
                )
            } catch (error) {
                reportNotificationError(error, 'persist-enable-preference')
            }

            return {
                success: true,
                token,
                registeredWithBackend: true,
            }
        } catch (error) {
            notificationsEnabled.value = false
            reportNotificationError(error, 'request-permission')
            return { success: false, error }
        }
    }

    // Try to register any unregistered token with the backend
    const syncTokenWithBackend = async () => {
        if (!isBrowserSupported.value) {
            notificationsEnabled.value = false
            return false
        }
        console.log(
            'Syncing notification token with backend',
            currentFcmToken.value,
            'isTokenRegisteredWithBackend',
            isTokenRegisteredWithBackend.value
        )
        // Determine currently logged-in backend user
        let currentUserId = null
        try {
            const { useAuthStore } = await import('@/stores/auth.store')
            const authStore = useAuthStore()
            currentUserId = authStore.userId
        } catch (_) {}

        const needsRegistration =
            currentFcmToken.value &&
            (!isTokenRegisteredWithBackend.value ||
                registeredUserId.value !== currentUserId)

        if (needsRegistration) {
            const success = await registerTokenWithBackend(
                currentFcmToken.value
            )
            if (!success) {
                notificationsEnabled.value = false
            }
            return success
        }
        const registered =
            !!currentFcmToken.value && isTokenRegisteredWithBackend.value
        notificationsEnabled.value = registered
        return registered
    }

    // Disable notifications and unregister token
    const disableNotifications = async () => {
        try {
            if (currentFcmToken.value && isTokenRegisteredWithBackend.value) {
                await notificationService.unregisterToken(currentFcmToken.value)
                isTokenRegisteredWithBackend.value = false
                registeredUserId.value = null
            }

            // Always attempt to delete the browser/device token so the browser stops receiving pushes
            try {
                const { deleteFCMToken } = await ensureFirebase()
                await deleteFCMToken()
            } catch (e) {
                console.warn('Deleting FCM token failed or not needed', e)
            }

            // Reset local state
            notificationsEnabled.value = false
            currentFcmToken.value = null

            // Persist preference on user document (best-effort)
            try {
                const UserService = await getUserService()
                UserService.updateUser({ notificationsEnabled: false }).catch(
                    () => {}
                )
            } catch (error) {
                reportNotificationError(error, 'persist-disable-preference', {
                    stage: 'disable',
                })
            }

            return { success: true }
        } catch (error) {
            console.error('Failed to disable notifications:', error)
            reportNotificationError(error, 'disable-notifications')
            return { success: false, error }
        }
    }

    // Check if notifications are currently enabled
    const checkNotificationStatus = async () => {
        const supported = await ensureNotificationsSupported()
        if (!supported) {
            return false
        }
        if (typeof Notification === 'undefined') {
            notificationsEnabled.value = false
            return false
        }

        if (Notification.permission !== 'granted') {
            notificationsEnabled.value = false
            return false
        }

        if (!currentFcmToken.value) {
            const { getFCMToken } = await ensureFirebase()
            const token = await getFCMToken()
            if (!token) {
                notificationsEnabled.value = false
                return false
            }
            currentFcmToken.value = token
        }

        const registered = await syncTokenWithBackend()
        notificationsEnabled.value = !!registered
        return !!registered
    }

    // Register a callback to receive notifications
    const onNotification = (callback) => {
        notificationCallbacks.push(callback)

        // Return a function to unregister the callback
        return () => {
            const index = notificationCallbacks.indexOf(callback)
            if (index > -1) {
                notificationCallbacks.splice(index, 1)
            }
        }
    }

    // Check if we should show permission request
    const shouldShowPermissionRequest = () => {
        if (!isBrowserSupported.value) {
            return false
        }

        // Check if Notification API is available in the browser
        if (typeof Notification === 'undefined') {
            return false
        }

        // Check if permission is already granted
        if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted'
        ) {
            return false
        }

        // Check if permission was previously denied
        if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'denied'
        ) {
            return false
        }

        // Check if user has already been notified about permissions recently
        const lastDenied = localStorage.getItem('notificationPromptDismissed')
        if (lastDenied) {
            const lastDeniedTimestamp = parseInt(lastDenied)
            const lastDeniedDate = new Date(lastDeniedTimestamp)
            const now = new Date()
            const diffTime = Math.abs(now - lastDeniedDate)
            const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))

            if (diffHours < 8) {
                return false
            }
        }

        return true
    }

    // Logout notifications (unregister token but keep preference)
    const logoutNotifications = async () => {
        try {
            if (currentFcmToken.value && isTokenRegisteredWithBackend.value) {
                await notificationService.unregisterToken(currentFcmToken.value)
                isTokenRegisteredWithBackend.value = false
                registeredUserId.value = null
            }

            // Always attempt to delete the browser/device token
            try {
                const { deleteFCMToken } = await ensureFirebase()
                await deleteFCMToken()
            } catch (e) {
                console.warn('Deleting FCM token failed or not needed', e)
            }

            // Reset local state
            notificationsEnabled.value = false
            currentFcmToken.value = null

            return { success: true }
        } catch (error) {
            console.error('Failed to logout notifications:', error)
            reportNotificationError(error, 'logout-notifications')
            return { success: false, error }
        }
    }

    return {
        init,
        cleanup,
        requestPermission,
        disableNotifications,
        logoutNotifications,
        checkNotificationStatus,
        onNotification,
        shouldShowPermissionRequest,
        syncTokenWithBackend,
        isEnabled: notificationsEnabled,
        isTokenRegistered: isTokenRegisteredWithBackend,
        isSupported: isBrowserSupported,
    }
}
